import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { Calendar } from 'primereact/calendar';
import { InputSwitch } from 'primereact/inputswitch';
import { Dialog } from 'primereact/dialog';
import { useSelector, useDispatch } from 'react-redux';
import { loadStudentsRankingAsync, resetStudentsRanking, selectStudentsRanking, defaultStudentsRankingFilters } from '../dashboardSlice';
import { ModuleService } from '../../../service/ModuleService';

const moduleService = new ModuleService();

const percentageBody = (field) => (rowData) => {
    const value = rowData[field];
    return value != null ? `${value}%` : '—';
};

const nameBody = (rowData) => {
    const name = [rowData.firstName, rowData.lastName].filter(Boolean).join(' ');
    return name || rowData.email;
};

export const StudentsRankingTable = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { data, totalRecords, status, error, filters } = useSelector(selectStudentsRanking);
    const [moduleOptions, setModuleOptions] = useState([]);
    const [searchInput, setSearchInput] = useState(filters?.search ?? '');
    // O Calendar em modo range trata value=[null, null] como "já tem uma
    // data inicial selecionada" (props.value.length é truthy) e quebra ao
    // comparar .getTime() com o segundo elemento nulo — value precisa ser
    // null de verdade quando não há período selecionado ainda.
    const [dateRange, setDateRange] = useState(
        filters?.startDate && filters?.endDate
            ? [new Date(filters.startDate), new Date(filters.endDate)]
            : null
    );
    const [displayInfoDialog, setDisplayInfoDialog] = useState(false);
    const loading = status === 'loading';
    // Calculado uma vez só, no primeiro mount: chegou aqui por um "voltar"
    // (POP — botão de voltar do timeline de um aluno, ver StudentTimelinePage.js)
    // ou por uma navegação nova de verdade (PUSH — ex: clicou no menu do
    // sidebar)? Só no segundo caso força reset + busca nova, ignorando o que
    // já estava carregado. Mesmo padrão de UsersView.js/UsersGrid.
    const forceRefreshRef = useRef(history.action !== 'POP');

    useEffect(() => {
        moduleService.getModules(0, 0, 500, 'description', 1)
            .then(res => setModuleOptions((res.data || []).map(m => ({ label: m.description, value: m.id }))))
            .catch(() => setModuleOptions([]));
    }, []);

    // O status/filters ficam no Redux (state.dashboard.studentsRanking), que
    // sobrevive ao desmontar a tabela. Se já tinha carregado antes E foi um
    // "voltar" (POP), mantém como estava — senão (navegação nova de verdade,
    // ou nunca carregou ainda) reseta pro padrão e busca de novo.
    useEffect(() => {
        if (status === 'succeeded' && !forceRefreshRef.current) {
            return;
        }
        dispatch(resetStudentsRanking());
        setSearchInput('');
        setDateRange(null);
        dispatch(loadStudentsRankingAsync(defaultStudentsRankingFilters));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyFilters = (newFilters) => {
        dispatch(loadStudentsRankingAsync({ ...filters, ...newFilters }));
    };

    // Debounce: só busca 500ms depois que o usuário parar de digitar, em vez
    // de disparar uma requisição a cada tecla.
    useEffect(() => {
        const handle = setTimeout(() => {
            if (searchInput !== (filters?.search ?? '')) {
                applyFilters({ search: searchInput, offset: 0 });
            }
        }, 500);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);

    // Mesmo padrão do exemplo em UI Components > Menu (Menubar): filtro à
    // esquerda, busca à direita com ícone, dentro da barra do Menubar (sem
    // itens de navegação — só usamos os slots start/end).
    const filterStartTemplate = () => (
        <div className="flex flex-column gap-1">
            <label htmlFor="ranking-module-filter" className="text-xs text-color-secondary">Módulo</label>
            <Dropdown
                inputId="ranking-module-filter"
                value={filters?.moduleId ?? null}
                options={moduleOptions}
                onChange={(e) => applyFilters({ moduleId: e.value, offset: 0 })}
                placeholder="Todos os módulos"
                showClear
                filter
                disabled={loading}
                className="w-full md:w-20rem"
            />
        </div>
    );

    // Botão ao lado do input busca na hora, sem esperar os 500ms do debounce
    // — mesmo padrão do exemplo Loading em UI Components > Button (o próprio
    // botão troca o ícone por um spinner enquanto carrega).
    const onSearchClick = () => {
        applyFilters({ search: searchInput, offset: 0 });
    };

    const formatDateParam = (date) => {
        if (!date) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // O % de acerto passa a considerar só respostas dadas dentro do
    // intervalo (ver dashboard.controller.ts) — só dispara a busca quando
    // as duas datas estão preenchidas, ou quando o usuário limpa (showButtonBar).
    const onDateRangeChange = (e) => {
        const value = e.value ?? null;
        setDateRange(value);
        const [start, end] = value || [];
        if (start && end) {
            applyFilters({ startDate: formatDateParam(start), endDate: formatDateParam(end), offset: 0 });
        } else if (!start && !end) {
            applyFilters({ startDate: null, endDate: null, offset: 0 });
        }
    };

    const filterDateRangeTemplate = () => (
        <div className="flex flex-column gap-1">
            <label htmlFor="ranking-date-range" className="text-xs text-color-secondary">Período das respostas</label>
            <Calendar
                id="ranking-date-range"
                value={dateRange}
                onChange={onDateRangeChange}
                selectionMode="range"
                dateFormat="dd/mm/yy"
                placeholder="Todo o período"
                showButtonBar
                disabled={loading}
                className="w-full md:w-20rem"
            />
        </div>
    );

    // Numa linha própria, acima dos inputs (ver gridHeaderTemplate). Mesmo
    // componente InputSwitch usado em
    // ResidenceCombinationsChart.js/MedicalServicesUserChart.js, mas com
    // label visível — aqui tem espaço de sobra pra isso, diferente do
    // cabeçalho apertado dos cards de gráfico.
    const filterActiveOnlyTemplate = () => (
        <div className="flex align-items-center gap-2">
            <InputSwitch
                inputId="ranking-active-only"
                checked={!!filters?.activeOnly}
                onChange={(e) => applyFilters({ activeOnly: e.value, offset: 0 })}
                disabled={loading}
            />
            <label htmlFor="ranking-active-only" className="text-xs text-color-secondary">Somente ativos</label>
        </div>
    );

    const filterEndTemplate = () => (
        <div className="flex flex-column gap-1">
            <label htmlFor="ranking-search" className="text-xs text-color-secondary">Buscar aluno</label>
            <div className="flex gap-2">
                <InputText
                    id="ranking-search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Nome ou email"
                    disabled={loading}
                    className="w-full md:w-20rem"
                />
                <Button icon="pi pi-search" loading={loading} onClick={onSearchClick} />
            </div>
        </div>
    );

    // Mesma disposição da tela PAGES > Crud / Gestão de usuários / Gestão de
    // provas: filtros no header da própria DataTable, em vez de uma linha
    // separada acima da tabela.
    const gridHeaderTemplate = () => (
        <div className="flex flex-column gap-3">
            {filterActiveOnlyTemplate()}
            <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3">
                <div className="flex flex-wrap gap-3">
                    {filterStartTemplate()}
                    {filterDateRangeTemplate()}
                </div>
                {filterEndTemplate()}
            </div>
        </div>
    );

    return (
        <div className="grid crud-demo">
            <div className="col-12">
                <div className="card">
                    <div className="flex justify-content-between align-items-center mb-3">
                        <h5 className="m-0">
                            {filters?.moduleId ? 'Ranking de alunos — Módulo selecionado' : 'Ranking de alunos — Todos os módulos'}
                        </h5>
                        <div className="flex gap-1">
                            <Button
                                icon="pi pi-info-circle"
                                rounded
                                text
                                tooltip="Como esses números são calculados"
                                tooltipOptions={{ position: 'top' }}
                                onClick={() => setDisplayInfoDialog(true)}
                            />
                            <Button
                                icon="pi pi-refresh"
                                rounded
                                text
                                onClick={() => applyFilters({})}
                                disabled={loading}
                            />
                        </div>
                    </div>
                    {error ? (
                        <div className="flex flex-column align-items-center justify-content-center p-5">
                            <i className="pi pi-exclamation-triangle mb-3" style={{ fontSize: '2rem', color: 'var(--red-500)' }}></i>
                            <span className="text-lg">{error}</span>
                            <Button label="Tentar Novamente" className="p-button-text mt-3" onClick={() => applyFilters({})} />
                        </div>
                    ) : (
                        // Paginação e ordenação acontecem no servidor (lazy) — a lista
                        // de alunos pode ser grande, então nunca carregamos tudo de
                        // uma vez só pra depois paginar/ordenar no navegador.
                        // Efeito de carregamento em Skeleton (UI Components > Misc) em
                        // vez do spinner padrão: linhas "vazias" no formato final da
                        // tabela, com um Skeleton em cada célula.
                        <DataTable
                            value={loading ? Array.from({ length: filters?.limit ?? 20 }, (_, i) => ({ userId: `skeleton-${i}` })) : (Array.isArray(data) ? data : [])}
                            header={gridHeaderTemplate()}
                            dataKey="userId"
                            lazy
                            paginator
                            first={filters?.offset ?? 0}
                            rows={filters?.limit ?? 20}
                            totalRecords={totalRecords}
                            rowsPerPageOptions={[20, 50, 100]}
                            onPage={(e) => applyFilters({ offset: e.first, limit: e.rows })}
                            sortField={filters?.sortField ?? 'firstName'}
                            sortOrder={filters?.sortOrder ?? 1}
                            onSort={(e) => applyFilters({ sortField: e.sortField, sortOrder: e.sortOrder, offset: 0 })}
                            responsiveLayout="scroll"
                            emptyMessage="Nenhum aluno com respostas nesse filtro."
                        >
                            <Column field="firstName" header="Nome" body={loading ? () => <Skeleton /> : nameBody} sortable={!loading} />
                            <Column field="serviceName" header="Serviço" body={loading ? () => <Skeleton /> : undefined} sortable={!loading} />
                            <Column field="uf" header="Estado" body={loading ? () => <Skeleton /> : undefined} sortable={!loading} style={{ width: '8rem' }} />
                            <Column
                                field="quizCorrectPercentage"
                                header="Quiz — % de Acerto"
                                body={loading ? () => <Skeleton /> : percentageBody('quizCorrectPercentage')}
                                sortable={!loading}
                            />
                            <Column
                                field="practiceTestCorrectPercentage"
                                header="Simulado — % de Acerto"
                                body={loading ? () => <Skeleton /> : percentageBody('practiceTestCorrectPercentage')}
                                sortable={!loading}
                            />
                            <Column
                                header="Detalhamento"
                                style={{ width: '12rem', textAlign: 'center' }}
                                body={loading ? () => <Skeleton /> : (rowData) => (
                                    <div className="flex gap-1 justify-content-center">
                                        <Button
                                            icon="pi pi-history"
                                            rounded
                                            text
                                            tooltip="Ver progresso e histórico individual"
                                            tooltipOptions={{ position: 'top' }}
                                            onClick={() => history.push(`/students/${rowData.userId}/timeline`, { student: rowData })}
                                        />
                                        <Button
                                            icon="pi pi-chart-bar"
                                            rounded
                                            text
                                            tooltip="Ver espelhamento de competências"
                                            tooltipOptions={{ position: 'top' }}
                                            onClick={() => history.push(`/students/${rowData.userId}/competencies`, { student: rowData })}
                                        />
                                        <Button
                                            icon="pi pi-table"
                                            rounded
                                            text
                                            tooltip="Ver heatmap de desempenho"
                                            tooltipOptions={{ position: 'top' }}
                                            onClick={() => history.push(`/students/${rowData.userId}/heatmap`, { student: rowData })}
                                        />
                                    </div>
                                )}
                            />
                        </DataTable>
                    )}
                </div>
            </div>

            <Dialog
                header="Como esses números são calculados"
                visible={displayInfoDialog}
                onHide={() => setDisplayInfoDialog(false)}
                style={{ width: '32rem' }}
            >
                <ul className="line-height-3 pl-3 m-0">
                    <li>O <b>% de acerto</b> são respostas certas / total de respostas, calculado separadamente pra Quiz e pra Simulado — um aluno pode aparecer bem em um e mal no outro.</li>
                    <li>Só entram alunos com pelo menos <b>1 resposta</b> registrada (de Quiz ou de Simulado). Quem nunca respondeu nada não aparece no ranking.</li>
                    <li>Se o aluno respondeu a mesma questão mais de uma vez, só a resposta <b>mais recente</b> conta pro % de acerto.</li>
                    <li>Questões de <b>prova oral</b> não contam no % de acerto, nem do Quiz nem do Simulado.</li>
                    <li><b>Serviço</b> e <b>Estado</b> são o serviço médico mais recente vinculado ao aluno (não o histórico inteiro).</li>
                    <li>O filtro de <b>Módulo</b> restringe o cálculo de Quiz e Simulado só àquele módulo.</li>
                    <li>O filtro <b>Somente ativos</b> mostra só quem tem assinatura ativa e ainda dentro da validade (não vencida) neste momento.</li>
                    <li>O filtro de <b>Período</b> restringe o cálculo às respostas dadas dentro do intervalo — não é a data de matrícula do aluno, é quando cada resposta foi registrada.</li>
                </ul>
            </Dialog>
        </div>
    );
};
